/**
 * PDF Export for Solar Design
 * Extracted from SolarDesignPage.js for maintainability
 * Uses native Canvas API + DOMMatrix for accurate Leaflet map capture
 * DO NOT use html2canvas — it breaks with Leaflet translate3d transforms
 */
import jsPDF from "jspdf";
import { buildScene, renderToDataURL } from "./scene3d";
import { panelKey } from "@/utils/panelCalc";

const ROOF_COLOR = "rgba(245,158,11,1)";
const OBSTACLE_COLOR = "#EF4444";

export async function exportSolarPDF({ mapContainerRef, mapRef, layout, spec, roofPolygons, roofSpecs = [], obstaclePolygons, obstacleHeights = [], removedKeys = new Set(), designName, clientName, searchQuery, moduleBrand, annualKwh = 0, inverterAcKw = 0 }) {
  const mapEl = mapContainerRef.current;
  const map = mapRef.current;
  let mapImage = null;
  const shownPanels = (layout.panels || []).filter(p => !removedKeys.has(panelKey(p)));

  try {
    const w = mapEl.offsetWidth;
    const h = mapEl.offsetHeight;
    const mapRect = mapEl.getBoundingClientRect();

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // Draw tile images
    const tiles = mapEl.querySelectorAll(".leaflet-tile-pane img");
    for (const tile of tiles) {
      if (!tile.complete || !tile.naturalWidth) continue;
      const tileRect = tile.getBoundingClientRect();
      const x = tileRect.left - mapRect.left;
      const y = tileRect.top - mapRect.top;
      try {
        ctx.drawImage(tile, x, y, tileRect.width, tileRect.height);
      } catch (e) {
        // CORS tainted tile — skip
      }
    }

    // Read tile pane transform offset for overlay alignment
    const tilePane = mapEl.querySelector(".leaflet-tile-pane");
    let tilePaneOffX = 0, tilePaneOffY = 0;
    if (tilePane) {
      const transform = window.getComputedStyle(tilePane).transform;
      if (transform && transform !== "none") {
        const matrix = new DOMMatrix(transform);
        tilePaneOffX = matrix.m41;
        tilePaneOffY = matrix.m42;
      }
    }

    const toPixel = (lat, lng) => {
      const pt = map.latLngToContainerPoint([lat, lng]);
      return { x: pt.x + tilePaneOffX, y: pt.y + tilePaneOffY };
    };

    // Draw roof polygons
    ctx.strokeStyle = ROOF_COLOR;
    ctx.lineWidth = 3;
    ctx.setLineDash([12, 5]);
    roofPolygons.forEach(pts => {
      ctx.beginPath();
      pts.forEach((p, i) => {
        const { x, y } = toPixel(p.lat, p.lng);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = "rgba(245,158,11,0.15)";
      ctx.fill();
      ctx.stroke();
    });
    ctx.setLineDash([]);

    // Draw obstacle polygons (keep-outs = height 0 → grey, no shadow)
    obstaclePolygons.forEach((pts, oi) => {
      const isKeepout = (obstacleHeights[oi] != null ? obstacleHeights[oi] : 1) <= 0;
      ctx.beginPath();
      pts.forEach((p, i) => {
        const { x, y } = toPixel(p.lat, p.lng);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = isKeepout ? "rgba(107,114,128,0.25)" : "rgba(239,68,68,0.3)";
      ctx.fill();
      ctx.strokeStyle = isKeepout ? "#6B7280" : OBSTACLE_COLOR;
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Draw solar panels
    ctx.fillStyle = "rgba(59,130,246,0.6)";
    ctx.strokeStyle = "rgba(59,130,246,0.85)";
    ctx.lineWidth = 1;
    shownPanels.forEach(panel => {
      ctx.beginPath();
      panel.corners.forEach((c, i) => {
        const { x, y } = toPixel(c.lat, c.lng);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });

    mapImage = canvas.toDataURL("image/jpeg", 0.9);
  } catch (err) {
    console.error("Map capture failed:", err);
  }

  // Build PDF
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, M = 14;
  let y = 16;

  pdf.setFillColor(47, 125, 82); pdf.rect(0, 0, W, 14, "F");
  pdf.setTextColor(255, 255, 255); pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
  pdf.text("SunBiz — Preliminary Solar Layout Report", M, 9.5);

  y = 22;
  pdf.setTextColor(27, 43, 34); pdf.setFontSize(14); pdf.setFont("helvetica", "bold");
  pdf.text(designName || "Solar Design", M, y); y += 7;

  pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(90, 114, 100);
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  if (clientName) { pdf.text(`Client: ${clientName}`, M, y); y += 5; }
  if (searchQuery) { pdf.text(`Location: ${searchQuery}`, M, y); y += 5; }
  pdf.text(`Generated: ${today}`, M, y); y += 9;

  pdf.setDrawColor(220, 230, 220); pdf.line(M, y, W - M, y); y += 6;

  pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(27, 43, 34);
  pdf.text("Capacity Summary", M, y); y += 6;

  const tableConfig = spec.modulesPerGroup > 1 ? `${spec.modulesPerGroup}${spec.orientation === "portrait" ? "P" : "L"}` : "1P";
  const rows = [
    ["Total Modules", `${layout.totalPanels} panels`],
    ["System Capacity", `${layout.totalKwp} kWp`],
    ["Est. Annual Generation", `${annualKwh.toLocaleString("en-IN")} kWh/yr`],
    ["Recommended Inverter (AC)", `${inverterAcKw} kW`],
    ["Panel Wattage", `${spec.panelWattageW} Wp`],
    ["Panel Dimensions", `${spec.panelLengthM}m × ${spec.panelWidthM}m (${spec.orientation})`],
    ...(moduleBrand ? [["Module Brand", moduleBrand]] : []),
    ["Table Configuration", `${tableConfig} — ${spec.modulesPerGroup} module${spec.modulesPerGroup > 1 ? "s" : ""} per table`],
    ["Roof Area (approx.)", `${layout.totalAreaM2} m²`],
    ["Tilt / Azimuth", `${spec.tiltDeg}° / ${spec.azimuthDeg}°`],
    ["Inter-Row Spacing", `${spec.interRowSpacingM} m`],
    ["Edge Setback", `${spec.setbackM} m`],
  ];
  pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
  rows.forEach(([label, val], i) => {
    const ry = y + i * 7;
    pdf.setFillColor(i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 245 : 255);
    pdf.rect(M, ry - 4, W - M * 2, 7, "F");
    pdf.setTextColor(90, 114, 100); pdf.text(label, M + 2, ry);
    pdf.setTextColor(27, 43, 34); pdf.setFont("helvetica", "bold"); pdf.text(val, 130, ry);
    pdf.setFont("helvetica", "normal");
  });
  y += rows.length * 7 + 8;

  pdf.setFontSize(8); pdf.setTextColor(138, 164, 150); pdf.setFont("helvetica", "italic");
  pdf.text("* Preliminary estimate. Actual capacity may vary based on site conditions and shading.", M, y, { maxWidth: W - M * 2 });
  y += 10;

  if (mapImage) {
    pdf.setFillColor(47, 125, 82); pdf.rect(0, y - 2, W, 8, "F");
    pdf.setTextColor(255, 255, 255); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text("Panel Layout — Satellite View", M, y + 3.5); y += 10;
    const imgW = W - M * 2;
    const imgH = Math.min(imgW * (mapEl.offsetHeight / mapEl.offsetWidth), 297 - y - 14);
    pdf.addImage(mapImage, "JPEG", M, y, imgW, imgH);
  }

  const footer = () => { pdf.setFontSize(7); pdf.setTextColor(138, 164, 150); pdf.setFont("helvetica", "normal"); pdf.text("Generated by SunBiz by Sun Bizcon — sunbizcon.com", M, 290); };
  footer();

  // Page 2 — 3D impression (isometric + from the south)
  try {
    const scene = buildScene({ roofPolygons, roofSpecs, obstaclePolygons, obstacleHeights, panels: layout.panels || [], removedKeys, spec });
    if (scene) {
      const iso = renderToDataURL(scene, 1400, 900, "iso");
      const south = renderToDataURL(scene, 1400, 700, "south");
      pdf.addPage();
      pdf.setFillColor(47, 125, 82); pdf.rect(0, 0, W, 14, "F");
      pdf.setTextColor(255, 255, 255); pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
      pdf.text("3D Impression — Preliminary", M, 9.5);
      let y2 = 22;
      pdf.setTextColor(90, 114, 100); pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
      pdf.text(`${designName || "Solar Design"} · ${shownPanels.length} panels · ${layout.totalKwp} kWp · tilt ${spec.tiltDeg}° · azimuth ${spec.azimuthDeg}°`, M, y2); y2 += 6;
      const imgW = W - M * 2;
      if (iso) { pdf.addImage(iso, "JPEG", M, y2, imgW, imgW * 900 / 1400); y2 += imgW * 900 / 1400 + 6; }
      if (south) { pdf.addImage(south, "JPEG", M, y2, imgW, imgW * 700 / 1400); y2 += imgW * 700 / 1400 + 4; }
      pdf.setFontSize(8); pdf.setTextColor(138, 164, 150); pdf.setFont("helvetica", "italic");
      pdf.text("Heights taken from the design's Roof Height, Parapet and obstacle heights. Indicative only — not an engineering drawing.", M, Math.min(y2 + 2, 284), { maxWidth: W - M * 2 });
      footer();
    }
  } catch (err) {
    console.error("3D page failed:", err);
  }

  const fname = `${(designName || "solar-design").replace(/\s+/g, "-").toLowerCase()}.pdf`;
  pdf.save(fname);
}
